import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { environment } from '@environments/environment'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { ISmsEventsResponse, ISmsMetricsResponse, ITrustedGenderList, IUpdateGenderList, LivePlannedEvents } from '../interfaces/metric.interface'

/**
 * Service that handles total events and workspaces requests and statistics
 */
@Injectable({
  providedIn: 'root'
})
export class MetricsService {
  /**
   * Creates the service & injects it's dependencies
   * @param http HttpClient
   */
  constructor(private http: HttpClient) {}
  /**
   * Gets the total counts for events per year
   * @param year year number
   * @returns Observable
   */
  totalEvents(year: number) {
    return this.http.get(`${environment.planningApi}events/totalCount?year=${year}`)
  }
  /**
   * Gets the total counts for workspaces per year
   * @param year year number
   * @returns Observable
   */
  totalWorkspaces(year: number) {
    return this.http.get(`${environment.planningApi}workspaces/totalCount?year=${year}`)
  }

  /**
   * Gets the total live and planned events for a particular year
   * @param year year number
   * @returns Observable of LivePlannedEvents
   */
  totalLiveAndPlannedEvents(year: number): Observable<LivePlannedEvents> {
    return this.http.get<LivePlannedEvents>(`${environment.planningApi}events/statistics?year=${year}`).pipe(
      map((res) => {
        res.data.map((record) => (record['collect_payment'] = record['collect_payment'] ? 'Yes' : 'No'))
        return res
      })
    )
  }

  /**
   * Updates a gender list record with a specified name and gender
   * @param name the name of the record to update
   * @param gender the new gender value for the record
   * @returns Observable of IUpdateGenderList
   */
  updateGenderList(name: string, gender: string, id: string): Observable<IUpdateGenderList> {
    const first_name = name.toLowerCase()
    gender = gender.toLowerCase()
    const body = { first_name, gender }
    return this.http.put<IUpdateGenderList>(`${environment.registrationsApi}gender-list/${id}`, body)
  }

  /**
   * Fetches the gender list with given pagination parameters
   * @param perpage number of records per page
   * @param page number of the current page
   * @returns Observable of ITrustedGenderList
   */
  genderList(perpage: number, page: number): Observable<ITrustedGenderList> {
    return this.http.get<ITrustedGenderList>(`${environment.registrationsApi}gender-list?perpage=${perpage}&page=${page}`)
  }

  /**
   * Searches the gender list for a given first name
   * @param first_name the first name to search in the gender list
   * @returns Observable of ITrustedGenderList
   */
  searchGenderList(first_name: string): Observable<ITrustedGenderList> {
    return this.http.get<ITrustedGenderList>(`${environment.registrationsApi}gender-list?first_name=${first_name}`)
  }

  /**
   * Deletes a gender record for a given name
   * @param name the name of the record to delete
   * @returns Observable of message
   */
  deleteGenderRecord(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.registrationsApi}gender-list/${id}`)
  }

  /**
   * Creates a new gender record with a given name and gender
   * @param name the name for the new gender record
   * @param gender the gender for the new gender record
   * @returns Observable of ITrustedGenderList
   */
  createName(name: string, gender: string): Observable<ITrustedGenderList> {
    const first_name = name.toLowerCase()
    gender = gender.toLowerCase()
    const body = { first_name, gender }
    return this.http.post<ITrustedGenderList>(`${environment.registrationsApi}gender-list`, body)
  }

  /**
   * Get the total number of events for a given year
   * @param year number - year to get the metrics for
   * @returns Observable of any
   */
  getSmsEvents(year: number): Observable<ISmsEventsResponse> {
    return this.http.get<ISmsEventsResponse>(`${environment.planningApi}events/sms-cost?year=${year}`)
  }

  /**
   * Refreshes SMS metrics for a specific event
   * @param workspaceSlug workspace slug
   * @param eventSlug event slug
   * @returns Observable of ISmsMetricsResponse
   */
  refreshSmsMetrics(workspaceSlug: string, eventSlug: string): Observable<ISmsMetricsResponse> {
    return this.http.post<ISmsMetricsResponse>(`${environment.registrationsApi}sms-metrics/${workspaceSlug}/${eventSlug}`, {})
  }

  /**
   * Gets the total live and planned events for a particular date
   * @param date date string
   * @returns Observable of LivePlannedEvents
   */
  totalLiveAndPlannedEventsByDate(date: string): Observable<LivePlannedEvents> {
    return this.http.get<LivePlannedEvents>(`${environment.planningApi}events/calendar?date=${date}`)
  }
}
